import React, { useRef, useEffect, useState } from "react";
import { FaDatabase, FaProjectDiagram, FaFileContract, FaCog, FaShieldAlt, FaChartLine } from "react-icons/fa";

// Color palette
const palette = {
  sectionBg: "linear-gradient(135deg, #3dc2ec 0%, #4b70f5 50%, #702fff 100%)", // gradient background
  cardBg: "transparent", // no card background
  iconGold: "#ffffff", // white accent for icons
  text: "#ffffff",
  subtitle: "#C9e8ff",
  cardTitle: "#fff",
  cardDesc: "#ffffff",
};

const functionalities = [
  {
    title: "Data Storage",
    description:
      "Data is stored using the medallion architecture, preparing it from raw to aggregated products for actionable insights.",
    icon: <FaDatabase size={36} color={palette.iconGold} />,
  },
  {
    title: "Data Flows",
    description:
      "Preconfigured and secured flows process data quickly and reliably, turning raw source data into final data products.",
    icon: <FaProjectDiagram size={36} color={palette.iconGold} />,
  },
  {
    title: "Data Monitoring",
    description:
      "AI-powered monitoring ensures the platform and data run smoothly and are fault-tolerant, taking proactive measures when needed.",
    icon: <FaChartLine size={36} color={palette.iconGold} />,
  },
  {
    title: "Data Insights",
    description:
      "Dashboards and reports provide the insights you need to make data-driven decisions across your company.",
    icon: <FaFileContract size={36} color={palette.iconGold} />,
  },
  {
    title: "Data Agentic Mesh",
    description:
      "Automated data management, insights, and actions on your data using AI reasoning. Let Agentic AI handle the heavy lifting.",
    icon: <FaCog size={36} color={palette.iconGold} />,
  },
  {
    title: "Data Infra Security",
    description:
      "A secure-by-design setup ensures data is only processed and used where allowed —no unknown access to your valuable data.",
    icon: <FaShieldAlt size={36} color={palette.iconGold} />,
  },
];

const PlatformFunctionalities = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Intersection Observer for fade-in
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 700;

  return (
    <section
      ref={sectionRef}
      className="page-content"
      style={{
        background: palette.sectionBg,
        borderRadius: isMobile ? 12 : 20,
        margin: isMobile ? "24px auto 0 auto" : "48px auto 0 auto",
        padding: isMobile ? "32px 0 24px 0" : "56px 0 48px 0",
        maxWidth: 1200,
        width: "95%",
        boxShadow: "0 4px 32px 0 rgba(0,0,0,0.10)",
        transition: "box-shadow 0.3s cubic-bezier(.4,2,.6,1)",
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transitionProperty: 'opacity, transform, box-shadow',
        transitionDuration: '0.9s',
        transitionTimingFunction: 'cubic-bezier(.4,2,.6,1)',
      }}
    >
      <div style={{...styles.headerWrap, margin: isMobile ? '0 auto 24px auto' : styles.headerWrap.margin}}>
        <span style={{
          ...styles.subtitle,
          fontSize: isMobile ? '1.05rem' : styles.subtitle.fontSize, // ~16-17px mobile
          letterSpacing: isMobile ? 0.8 : styles.subtitle.letterSpacing,
        }}>
          WHAT IS INSIDE
        </span>
        <h2 style={{
          ...styles.title,
          fontSize: isMobile ? '1.3rem' : styles.title.fontSize, // ~21px mobile
          margin: isMobile ? '0 0 10px 0' : styles.title.margin,
          lineHeight: isMobile ? 1.22 : styles.title.lineHeight,
        }}>
          Unlock Your Data Features
        </h2>
        <p style={{
          ...styles.desc,
          fontSize: isMobile ? '1.05rem' : styles.desc.fontSize, // ~16-17px mobile
          margin: isMobile ? '0 auto 0 auto' : styles.desc.margin,
          lineHeight: isMobile ? 1.5 : styles.desc.lineHeight,
        }}>
            Everything you need to move and get to value fast. Our platform provides six powerful, production-ready functionalities. So you can focus on creating value.
        </p>
      </div>
      <div style={{
        ...styles.gridWrap,
        gridTemplateColumns: isMobile ? '1fr' : styles.gridWrap.gridTemplateColumns,
        gap: isMobile ? 14 : styles.gridWrap.gap,
        rowGap: isMobile ? 18 : styles.gridWrap.rowGap,
        padding: isMobile ? '0 2px' : styles.gridWrap.padding
      }}>
        {functionalities.map((func, idx) => (
          <div key={idx} className="functionality-card" style={{
            ...styles.card,
            flexDirection: isMobile ? 'column' : styles.card.flexDirection,
            alignItems: isMobile ? 'center' : styles.card.alignItems,
            minHeight: isMobile ? 80 : styles.card.minHeight,
            padding: isMobile ? '8px 4px' : styles.card.padding
          }}>
            <div style={{...styles.iconWrap, padding: isMobile ? 8 : styles.iconWrap.padding, minWidth: isMobile ? 36 : styles.iconWrap.minWidth, minHeight: isMobile ? 36 : styles.iconWrap.minHeight}}>{func.icon}</div>
            <div>
              <h3 style={{
                ...styles.cardTitle,
                fontSize: isMobile ? '1rem' : styles.cardTitle.fontSize, // ~16px mobile
                margin: isMobile ? '6px 0 4px 0' : styles.cardTitle.margin,
                lineHeight: isMobile ? 1.3 : styles.cardTitle.lineHeight,
              }}>{func.title}</h3>
              <p style={{
                ...styles.cardDesc,
                fontSize: isMobile ? '0.98rem' : styles.cardDesc.fontSize, // ~15-16px mobile
                margin: isMobile ? '0' : styles.cardDesc.margin,
                lineHeight: isMobile ? 1.5 : styles.cardDesc.lineHeight,
              }}>{func.description}</p>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 700px) {
          .page-content .functionality-card h3 {
            font-size: 1.05rem !important;
            margin: 6px 0 4px 0 !important;
            line-height: 1.3 !important;
            text-align: center !important;
          }
          .page-content .functionality-card p {
            font-size: 0.98rem !important;
            margin: 0 !important;
            line-height: 1.5 !important;
            text-align: center !important;
          }
          .page-content .functionality-card {
            padding: 12px 6px !important;
          }
          .page-content .functionality-card .iconWrap {
            min-width: 48px !important;
            min-height: 48px !important;
            padding: 14px !important;
            margin-left: auto !important;
            margin-right: auto !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .page-content .functionality-card {
            min-height: 80px !important;
          }
          .page-content .functionality-card h2, .page-content .functionality-card h3, .page-content .functionality-card h4 {
            font-weight: 700 !important;
          }
        }
      `}</style>
      <style>{`
        .functionality-card {
          transition: box-shadow 0.22s cubic-bezier(.4,2,.6,1);
        }
        .functionality-card:hover {
          /* Removed transform and scale animation on hover */
          box-shadow: 0 8px 32px 0 rgba(231,201,139,0.10);
        }
        @media (max-width: 900px) {
          .functionality-card { min-height: 160px; }
        }
        @media (max-width: 800px) {
          .functionality-card { min-height: 140px; }
        }
        @media (max-width: 700px) {
          .functionality-card { min-height: 120px; }
        }
        @media (max-width: 600px) {
          .functionality-card { min-height: 100px; }
        }
      `}</style>
    </section>
  );
};

const styles = {
  headerWrap: {
    maxWidth: 900,
    textAlign: "center",
    margin: "0 auto 40px auto",
  },
  subtitle: {
    color: palette.subtitle,
    fontWeight: 700,
    fontSize: 16,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    marginBottom: 10,
    display: "inline-block",
    fontFamily: 'Inter, sans-serif',
  },
  title: {
    fontSize: 36,
    fontWeight: 700,
    color: palette.text,
    margin: "0 0 18px 0",
    lineHeight: 1.15,
    fontFamily: 'Inter, sans-serif',
  },
  desc: {
    color: palette.cardDesc,
    fontSize: 18,
    fontWeight: 400,
    margin: "0 auto 0 auto",
    maxWidth: 800,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.6,
    textAlign: 'center',
  },
  gridWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 36,
    width: "100%",
    maxWidth: 1000,
    margin: "0 auto",
    padding: "0 12px",
    rowGap: 40,
    // Responsive
    // On mobile, stack vertically
  },
  card: {
    background: palette.cardBg,
    borderRadius: 16,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 18,
    padding: "0 0 0 0",
    minHeight: 120,
    fontFamily: 'Inter, sans-serif',
    boxShadow: "none",
    transition: "box-shadow 0.2s cubic-bezier(.4,2,.6,1)",
  },
  iconWrap: {
    background: "#2a292e",
    borderRadius: "50%",
    padding: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 0,
    minWidth: 56,
    minHeight: 56,
    boxShadow: "0 2px 8px rgba(231,201,139,0.08)",
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: palette.cardTitle,
    margin: "0 0 8px 0",
    fontFamily: 'Inter, sans-serif',
  },
  cardDesc: {
    color: palette.cardDesc,
    fontSize: 15,
    fontWeight: 400,
    margin: 0,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    maxWidth: 320,
  },
};

// Responsive grid
styles['@media (max-width: 900px)'] = {
  gridWrap: {
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    rowGap: 32,
  },
};
styles['@media (max-width: 600px)'] = {
  gridWrap: {
    gridTemplateColumns: '1fr',
    gap: 16,
    rowGap: 24,
  },
  title: {
    fontSize: 24,
  },
  desc: {
    fontSize: 15,
  },
  cardTitle: {
    fontSize: 15,
  },
  cardDesc: {
    fontSize: 13,
  },
  iconWrap: {
    padding: 10,
    minWidth: 40,
    minHeight: 40,
  },
};

export default PlatformFunctionalities;
